import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FaSwimmingPool,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBan,
  FaTrash,
  FaPlus,
  FaHome,
  FaFilter,
  FaTicketAlt,
  FaRandom,
  FaTrophy,
} from "react-icons/fa";
import { useBuilding } from "../../context/BuildingContext";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { normalizeAmenity } from "./AmenityConfigTab";
import { tolerantId, fetchUsersByUserIds } from "../../utils/firestoreHelpers";
import { useLatestRate } from "../../utils/exchangeRates";
import { formatMoney } from "../../utils/format";
import AdminModal from "../../components/AdminModal";
import {
  colors,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  badgeBase,
} from "../../utils/designTokens";
import { STATUS_COLORS } from "../../utils/statusColors";
import { isDateBlocked } from "../../utils/amenityRules";

/** userid canónico para comparar: 102, "102" y "0102" son el mismo. */
const claveId = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

/**
 * Fichas de `users` por userid, nunca la colección entera. C10 de las reglas
 * 0D: el staff lee fichas ajenas solo con accessBids del edificio, así que van
 * por fetchUsersByUserIds(ids, edificio) de firestoreHelpers (accessBids +
 * userid in, en tandas). Si se niegan, se muestra el userid.
 * Devuelve un Map claveId → ficha.
 */
async function fichasPorUserid(rawIds, buildingId) {
  const ids = [
    ...new Set(
      (rawIds || []).filter((id) => id != null && String(id).trim() !== "").map((id) => String(id).trim())
    ),
  ];
  const fichas = new Map();
  if (!ids.length) return fichas;
  const lista = await fetchUsersByUserIds(ids, buildingId);
  (lista || []).forEach((u) => {
    const clave = claveId(u.userid);
    // Con varias fichas para el mismo userid (multi-rol), la de residente.
    if (!fichas.has(clave) || Number(u.typeuser ?? 0) === 0) fichas.set(clave, u);
  });
  return fichas;
}

/**
 * Una chance por persona en un sorteo: las anotaciones nuevas tienen id
 * determinístico (C8), pero las viejas (addDoc) pueden repetir el userid y
 * se cuentan una sola vez.
 */
export const participantesUnicos = (entries) => {
  const porPersona = new Map();
  (entries || []).forEach((e) => {
    if (e.status === "anulado") return;
    const k = claveId(e.userid);
    if (!porPersona.has(k)) porPersona.set(k, e);
  });
  return [...porPersona.values()];
};

// Nombres de meses en español
const monthNames = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
// Nombres de días abreviados
const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ReservasAmenities = ({ setSelected }) => {
  const { buildings, selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const { rate: bcuRate } = useLatestRate();
  const buildingFilter =
    selectedBuilding?.buildingid ||
    (buildings && buildings.length === 1 ? buildings[0].buildingid : null);

  // Estado del calendario
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  // Datos
  const [amenities, setAmenities] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [apartmentsMap, setApartmentsMap] = useState({});
  const [amenityChargesMap, setAmenityChargesMap] = useState({});
  const [signups, setSignups] = useState([]); // R6+: anotaciones a sorteos
  const [raffleWinners, setRaffleWinners] = useState({}); // groupKey -> userid tentativo (pre-confirmación)
  const [raffleBusy, setRaffleBusy] = useState(""); // groupKey en curso
  const [loading, setLoading] = useState(false);

  // Filtros
  const [filterAmenity, setFilterAmenity] = useState("Todos");
  const [filterUnit, setFilterUnit] = useState("Todos");

  // Modales
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockDate, setBlockDate] = useState(null);
  const [blockTurn, setBlockTurn] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveDate, setReserveDate] = useState(null);
  const [reserveFullDate, setReserveFullDate] = useState(""); // para "Nueva reserva" desde toolbar
  const [reserveTurn, setReserveTurn] = useState("");
  const [reserveApartment, setReserveApartment] = useState("");
  const [reserveResident, setReserveResident] = useState("");
  const [reserveByUnit, setReserveByUnit] = useState(false); // asociar a unidad sin residente
  const [reserveAmenityName, setReserveAmenityName] = useState(""); // amenity elegido en el modal

  // Modal de cancelación con motivo
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  // ============================================================
  // Carga de datos
  // ============================================================
  const fetchData = useCallback(async () => {
    if (!buildingFilter) return;
    setLoading(true);
    try {
      // Obtener amenities del edificio
      const amenQ = query(
        collection(db, "amenitiesbuilding"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      const amenSnap = await getDocs(amenQ);
      const amenList = amenSnap.docs.map((d) => normalizeAmenity({ id: d.id, ...d.data() }));
      setAmenities(amenList);

      // Obtener reservas
      const resQ = query(
        collection(db, "reservas"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      const resSnap = await getDocs(resQ);
      const reservasList = resSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReservas(reservasList);

      // Obtener apartamentos
      const aptQ = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(buildingFilter))
      );
      const aptSnap = await getDocs(aptQ);
      const aMap = {};
      aptSnap.docs.forEach((d) => {
        aMap[d.id] = { id: d.id, ...d.data() };
      });
      setApartmentsMap(aMap);

      // Obtener cargos de amenities
      const chargesQ = query(
        collection(db, "amenitycharges"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      let chargesSnap;
      try {
        chargesSnap = await getDocs(chargesQ);
      } catch { chargesSnap = { docs: [] }; }
      const cMap = {};
      chargesSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.reservaId) cMap[data.reservaId] = { id: d.id, ...data };
      });
      setAmenityChargesMap(cMap);

      // R6+: anotaciones a sorteos del edificio
      let signupsList = [];
      try {
        const suSnap = await getDocs(query(
          collection(db, "amenity_signups"),
          where("buildingid", "in", tolerantId(buildingFilter))
        ));
        signupsList = suSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch { signupsList = []; }
      setSignups(signupsList);

      // Nombres solo de quienes aparecen en reservas, unidades y sorteos (no
      // la colección users entera). Sin nombres, se muestra "ID x".
      const userIds = [
        ...reservasList.map((r) => r.userid),
        ...signupsList.map((s) => s.userid),
        ...Object.values(aMap).flatMap((a) => [a.owner, a.ownerSecondary, a.tenant, a.tenantSecondary]),
      ];
      try {
        const fichas = await fichasPorUserid(userIds, buildingFilter);
        const uMap = {};
        userIds.forEach((uid) => {
          const u = fichas.get(claveId(uid));
          if (u) uMap[String(uid)] = { name: u.NombreCompleto || u.name || u.email || "Sin nombre", email: u.email || "" };
        });
        setUsersMap(uMap);
      } catch (e) {
        console.warn("Sin nombres de residentes:", e?.code || e?.message || e);
        setUsersMap({});
      }
    } catch (err) {
      console.error("Error cargando datos:", err);
      showToast("Error al cargar datos", "error");
    } finally {
      setLoading(false);
    }
  }, [buildingFilter, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================
  // Helpers de calendario
  // ============================================================
  const goToPrevMonth = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear((y) => y - 1); }
    else setCalendarMonth((m) => m - 1);
  };
  const goToNextMonth = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear((y) => y + 1); }
    else setCalendarMonth((m) => m + 1);
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarMonth, calendarYear]);

  // Configuración del amenity seleccionado
  const selectedAmenityConfig = useMemo(() => {
    if (filterAmenity === "Todos") return null;
    return amenities.find((a) => (a.name || a.amenity) === filterAmenity) || null;
  }, [filterAmenity, amenities]);

  // Turnos activos para el amenity seleccionado (o valores por defecto si "Todos")
  const activeTurns = useMemo(() => {
    if (selectedAmenityConfig) return selectedAmenityConfig.turns || [];
    return [
      { name: "Mediodía", startTime: "12:00", endTime: "18:00" },
      { name: "Noche", startTime: "18:00", endTime: "00:00" },
    ];
  }, [selectedAmenityConfig]);

  // Turnos del modal de reserva (depende del amenity elegido en el modal)
  const modalTurns = useMemo(() => {
    const cfg = amenities.find((a) => (a.name || a.amenity) === reserveAmenityName);
    if (cfg) return cfg.turns || [];
    return activeTurns;
  }, [reserveAmenityName, amenities, activeTurns]);

  // Filtrar reservas por mes/año/amenity/unidad
  const filteredReservas = useMemo(() => {
    return reservas.filter((r) => {
      if (!r.date) return false;
      if (r.status === "cancelled") return false;
      const parts = r.date.split("-");
      const rYear = parseInt(parts[0], 10);
      const rMonth = parseInt(parts[1], 10) - 1;
      if (rYear !== calendarYear || rMonth !== calendarMonth) return false;
      if (filterAmenity !== "Todos" && r.amenity !== filterAmenity) return false;
      if (filterUnit !== "Todos") {
        const unit = getResidentUnit(r.userid);
        if (unit !== filterUnit) return false;
      }
      return true;
    });
  }, [reservas, calendarMonth, calendarYear, filterAmenity, filterUnit]);

  // Agrupar reservas por día
  const reservasByDay = useMemo(() => {
    const map = {};
    filteredReservas.forEach((r) => {
      const day = parseInt(r.date.split("-")[2], 10);
      if (!map[day]) map[day] = [];
      map[day].push(r);
    });
    return map;
  }, [filteredReservas]);

  // Estadísticas resumidas
  const totalReservas = filteredReservas.filter((r) => r.status !== "blocked").length;
  const totalBlocked = filteredReservas.filter((r) => r.status === "blocked").length;
  const ingresosEstimados = useMemo(() => {
    let total = 0;
    filteredReservas.forEach((r) => {
      if (r.status === "blocked") return;
      let cost = 0;
      let currency = "UYU";
      const charge = amenityChargesMap[r.id];
      if (charge) {
        cost = Number(charge.cost) || 0;
        currency = (charge.currency || "UYU").toUpperCase();
      } else {
        const config = amenities.find((a) => (a.name || a.amenity) === r.amenity);
        if (config && config.category === "con_costo") {
          const turn = (config.turns || []).find((t) => t.name === r.turno || r.turno?.includes(t.name));
          if (turn) cost = Number(turn.cost) || 0;
          currency = (config.currency || "UYU").toUpperCase();
        }
      }
      if (cost <= 0) return;
      // Convertir a UYU usando tasa BCU
      if (currency === "UI" && bcuRate?.ui) {
        total += cost * bcuRate.ui;
      } else if (currency === "UR" && bcuRate?.ur) {
        total += cost * bcuRate.ur;
      } else if (currency === "USD" && (bcuRate?.sell || bcuRate?.buy)) {
        total += cost * (bcuRate.sell || bcuRate.buy);
      } else {
        total += cost;
      }
    });
    return Math.round(total);
  }, [filteredReservas, amenityChargesMap, amenities, bcuRate]);

  // Nombres únicos de amenities para el selector
  const amenityNames = useMemo(() => {
    const names = amenities.map((a) => a.name || a.amenity).filter(Boolean);
    return ["Todos", ...names];
  }, [amenities]);

  // Nombres únicos de unidades para el selector
  const unitNames = useMemo(() => {
    const units = new Set();
    Object.values(apartmentsMap).forEach((apt) => {
      const label = apt.name || apt.unit || apt.number || "";
      if (label) units.add(label);
    });
    return ["Todos", ...Array.from(units).sort()];
  }, [apartmentsMap]);

  // Resolver unidad del residente
  const getResidentUnit = (userid) => {
    const uid = String(userid ?? "").trim();
    if (!uid) return "—";
    for (const apt of Object.values(apartmentsMap)) {
      if (
        String(apt.owner) === uid ||
        String(apt.ownerSecondary) === uid ||
        String(apt.tenant) === uid ||
        String(apt.tenantSecondary) === uid
      ) {
        return apt.name || apt.unit || apt.number || "—";
      }
    }
    return "—";
  };

  // Resolver tipo de residente (propietario / inquilino)
  const getResidentType = (userid) => {
    const uid = String(userid ?? "").trim();
    if (!uid) return "—";
    for (const apt of Object.values(apartmentsMap)) {
      if (String(apt.owner) === uid || String(apt.ownerSecondary) === uid) return "Propietario";
      if (String(apt.tenant) === uid || String(apt.tenantSecondary) === uid) return "Inquilino";
    }
    return "—";
  };

  // Número de unidad por apartmentId directo (para reservas byUnit)
  const getUnitNumberById = (aptId) => {
    const apt = aptId ? apartmentsMap[aptId] : null;
    return apt ? (apt.name || apt.unit || apt.number || aptId) : "—";
  };

  // Obtener costo de una reserva
  const getReservaCost = (reserva) => {
    const charge = amenityChargesMap[reserva.id];
    if (charge) return { cost: Number(charge.cost) || 0, currency: charge.currency || "UYU" };
    const config = amenities.find((a) => (a.name || a.amenity) === reserva.amenity);
    if (config && config.category === "con_costo") {
      const turn = (config.turns || []).find((t) => t.name === reserva.turno || reserva.turno?.includes(t.name));
      if (turn) return { cost: Number(turn.cost) || 0, currency: config.currency || "UYU" };
    }
    return { cost: 0, currency: "UYU" };
  };

  // Verificar si un día está disponible para el amenity seleccionado
  const isDayAvailable = (dayNum) => {
    if (!selectedAmenityConfig) return true;
    const date = new Date(calendarYear, calendarMonth, dayNum);
    const dow = date.getDay();
    return (selectedAmenityConfig.availableDays || [0, 1, 2, 3, 4, 5, 6]).includes(dow);
  };

  // Obtener estado de un slot (día + turno)
  const getSlotStatus = (day, turnName) => {
    const dayReservas = reservasByDay[day] || [];
    const slotReservas = dayReservas.filter((r) => {
      const rTurno = r.turno || "";
      return rTurno === turnName || rTurno.includes(turnName);
    });
    const blocked = slotReservas.find((r) => r.status === "blocked");
    if (blocked) return { status: "blocked", reservas: slotReservas, blocked };
    const confirmed = slotReservas.filter((r) => r.status !== "blocked");
    if (confirmed.length > 0) return { status: "reserved", reservas: confirmed, count: confirmed.length };
    return { status: "available", reservas: [], count: 0 };
  };

  // ============================================================
  // Acciones de administrador
  // ============================================================
  const handleCancelReservation = (reserva) => {
    setCancelTarget(reserva);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const confirmCancelReservation = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      showToast("Ingresá un motivo de cancelación", "warning");
      return;
    }
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, "reservas", cancelTarget.id), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        cancelledBy: "admin",
        cancelledByUser: user?.email || "admin",
        cancelReason: cancelReason.trim(),
      });
      // Reversar cargo si existe
      const charge = amenityChargesMap[cancelTarget.id];
      if (charge && charge.id && charge.status !== "reversed") {
        await updateDoc(doc(db, "amenitycharges", charge.id), {
          status: "reversed",
          reversedAt: serverTimestamp(),
        });
      }
      // Cancelar tareas de limpieza automáticas asociadas (con el edificio en
      // la consulta: la regla del staff lo mira en cada tarea). El trigger
      // onAmenityReservationCancelled hace lo mismo al pasar a cancelled; las
      // dos cancelaciones son idempotentes (solo tocan las no completadas).
      try {
        const taskQ = query(
          collection(db, "employee_tasks"),
          where("reservaId", "==", cancelTarget.id),
          where("autoGenerated", "==", true),
          where("buildingid", "in", tolerantId(cancelTarget.buildingid ?? buildingFilter))
        );
        const taskSnap = await getDocs(taskQ);
        for (const taskDoc of taskSnap.docs) {
          if (taskDoc.data().status !== "completada") {
            await updateDoc(doc(db, "employee_tasks", taskDoc.id), {
              status: "cancelada",
              cancelledAt: serverTimestamp(),
            });
          }
        }
      } catch (taskErr) {
        console.warn("Error cancelando tareas de limpieza:", taskErr);
      }

      showToast("Reserva cancelada", "success");
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason("");
      fetchData();
      setSelectedDayDetail(null);
    } catch (err) {
      console.error("Error cancelando reserva:", err);
      showToast("Error al cancelar reserva", "error");
    }
  };

  const handleBlockSlot = async () => {
    if (!blockDate || !blockReason.trim()) {
      showToast("Completá la fecha y el motivo", "warning");
      return;
    }
    try {
      const user = auth.currentUser;
      const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(blockDate).padStart(2, "0")}`;
      const amenityName = filterAmenity !== "Todos" ? filterAmenity : amenities[0]?.name || "General";
      const config = amenities.find((a) => (a.name || a.amenity) === amenityName);
      // FIX: "Todo el día" (blockTurn vacío) antes guardaba turno:"Todo el día",
      // que NUNCA matcheaba las queries de ocupación por turno → no bloqueaba nada.
      // Ahora creamos un doc de bloqueo POR CADA turno del amenity (o el turno elegido),
      // y siempre guardamos amenityId.
      const turnsToBlock = blockTurn
        ? [blockTurn]
        : (config?.turns || []).map((t) => t.name).filter(Boolean);
      if (turnsToBlock.length === 0) {
        showToast("El amenity no tiene turnos configurados para bloquear", "warning");
        return;
      }
      await Promise.all(
        turnsToBlock.map((turnName) =>
          addDoc(collection(db, "reservas"), {
            buildingid: Number(buildingFilter),
            amenity: amenityName,
            amenityId: config?.id || "",
            date: dateStr,
            turno: turnName,
            status: "blocked",
            blockReason: blockReason.trim(),
            blockAllDay: !blockTurn,
            createdBy: user?.email || "admin",
            createdAt: serverTimestamp(),
          })
        )
      );
      showToast(blockTurn ? "Turno bloqueado" : "Día bloqueado (todos los turnos)", "success");
      setShowBlockModal(false);
      setBlockReason("");
      fetchData();
    } catch (err) {
      console.error("Error bloqueando turno:", err);
      showToast("Error al bloquear turno", "error");
    }
  };

  const handleUnblock = async (reserva) => {
    const ok = await confirmDialog("¿Desbloquear este turno?", { title: "Desbloquear turno" });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "reservas", reserva.id));
      showToast("Turno desbloqueado", "success");
      fetchData();
      setSelectedDayDetail(null);
    } catch (err) {
      console.error("Error desbloqueando:", err);
      showToast("Error al desbloquear", "error");
    }
  };

  const handleReserveOnBehalf = async () => {
    const dateStr = reserveDate
      ? `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(reserveDate).padStart(2, "0")}`
      : reserveFullDate;
    if (!dateStr || !reserveAmenityName || !reserveTurn || (!reserveByUnit && !reserveResident)) {
      showToast("Completá todos los campos", "warning");
      return;
    }
    if (reserveByUnit && !reserveApartment) {
      showToast("Seleccioná una unidad para asociar la reserva", "warning");
      return;
    }
    try {
      const user = auth.currentUser;
      const amenityName = reserveAmenityName || (filterAmenity !== "Todos" ? filterAmenity : amenities[0]?.name || "General");
      const config = amenities.find((a) => (a.name || a.amenity) === amenityName);
      // R6: si la fecha está bloqueada, el admin puede reservar igual (override) confirmando.
      if (isDateBlocked(config, dateStr)) {
        const ok = await confirmDialog(
          `El ${dateStr} está marcado como fecha bloqueada para "${amenityName}". ¿Reservar de todos modos?`,
          { title: "Fecha bloqueada", confirmLabel: "Reservar igual", danger: true }
        );
        if (!ok) return;
      }
      const reservaRef = doc(collection(db, "reservas"));
      // Validacion de disponibilidad fuera de la transaccion
      // (tx.get() no soporta Query objects en Firestore Web SDK)
      const existingQ = query(
        collection(db, "reservas"),
        where("buildingid", "==", Number(buildingFilter)),
        where("amenity", "==", amenityName),
        where("date", "==", dateStr),
        where("turno", "==", reserveTurn)
      );
      const existingSnap = await getDocs(existingQ);
      const confirmedSlots = existingSnap.docs.filter((d) => d.data().status !== "cancelled");
      if (config?.exclusive && confirmedSlots.length > 0) {
        throw new Error("El turno ya se encuentra reservado.");
      }
      if (!config?.exclusive && config?.maxCapacity && confirmedSlots.length >= config.maxCapacity) {
        throw new Error("El turno alcanzó la capacidad máxima.");
      }

      const effectiveUserid = reserveByUnit ? "" : reserveResident;
      await runTransaction(db, async (tx) => {
        tx.set(reservaRef, {
          buildingid: Number(buildingFilter),
          amenity: amenityName,
          amenityId: config?.id || "",
          date: dateStr,
          turno: reserveTurn,
          userid: effectiveUserid,
          apartmentId: reserveApartment,
          byUnit: reserveByUnit || false,
          status: "confirmed",
          createdBy: user?.email || "admin",
          createdAt: serverTimestamp(),
        });

        if (config && config.category === "con_costo") {
          const turn = (config.turns || []).find((t) => t.name === reserveTurn);
          const cost = turn ? Number(turn.cost) || 0 : 0;
          if (cost > 0) {
            const parsedDate = new Date(calendarYear, calendarMonth, reserveDate);
            const chargeRef = doc(collection(db, "amenitycharges"));
            tx.set(chargeRef, {
              reservaId: reservaRef.id,
              amenityId: config.id,
              userid: effectiveUserid,
              apartmentId: reserveApartment,
              byUnit: reserveByUnit || false,
              buildingid: Number(buildingFilter),
              amenity: amenityName,
              turno: reserveTurn,
              date: dateStr,
              cost: cost,
              currency: config.currency || "UYU",
              concept: `Reserva de ${amenityName} - ${dateStr} - Turno ${reserveTurn}`,
              residentType: getResidentType(reserveResident),
              status: "pending",
              month: parsedDate.getMonth() + 1,
              year: parsedDate.getFullYear(),
              targetFundId: config.targetFundId || "reserva",
              createdAt: serverTimestamp(),
            });
          }
        }
      });

      // La tarea de limpieza (amenity con requiresCleaning) la crea el servidor:
      // onAmenityReservationCleaningTasks, al crearse la reserva confirmada, con
      // id determinístico limpieza_<reserva>_<employeeid> (C11/D3). Si también
      // la creara el cliente, el colaborador recibiría dos tareas iguales.

      showToast("Reserva creada", "success");
      setShowReserveModal(false);
      setReserveApartment("");
      setReserveResident("");
      fetchData();
    } catch (err) {
      console.error("Error creando reserva:", err);
      showToast("Error al crear reserva", "error");
    }
  };

  // ============================================================
  // R6+: Sorteos de amenities
  // ============================================================
  // Agrupa las anotaciones por (amenity, fecha). Un grupo con status "ganador" ya está resuelto.
  const raffleGroups = useMemo(() => {
    const groups = {};
    (signups || []).forEach((s) => {
      if (s.status === "anulado") return;
      const key = `${s.amenityId || s.amenityName}__${s.date}`;
      if (!groups[key]) groups[key] = { key, amenityId: s.amenityId, amenityName: s.amenityName, date: s.date, entries: [], resolved: false, winner: null };
      groups[key].entries.push(s);
      if (s.status === "ganador") { groups[key].resolved = true; groups[key].winner = s; }
    });
    return Object.values(groups)
      .map((g) => ({ ...g, participantes: participantesUnicos(g.entries) }))
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [signups]);

  // Costo de uso del amenity para un sorteo (día completo): el turno más caro (o costDay).
  const raffleCostFor = (amenityName) => {
    const config = amenities.find((a) => (a.name || a.amenity) === amenityName);
    if (!config || config.category !== "con_costo") return { cost: 0, currency: "UYU", config };
    const cost = Math.max(0, ...(config.turns || []).map((t) => Number(t.cost) || 0), Number(config.costDay) || 0);
    return { cost, currency: config.currency || "UYU", config };
  };

  const handleSortear = (group) => {
    const pool = participantesUnicos(group.entries);
    if (pool.length === 0) return;
    const winner = pool[Math.floor(Math.random() * pool.length)];
    setRaffleWinners((prev) => ({ ...prev, [group.key]: winner.userid }));
  };

  const handleConfirmWinner = async (group) => {
    const winnerUserid = raffleWinners[group.key];
    if (!winnerUserid) { showToast("Sorteá primero para elegir un ganador.", "warning"); return; }
    const winnerEntry = group.entries.find((e) => claveId(e.userid) === claveId(winnerUserid));
    if (!winnerEntry) return;
    const { cost, currency, config } = raffleCostFor(group.amenityName);
    const name = usersMap[String(winnerUserid)]?.name || `ID ${winnerUserid}`;
    const ok = await confirmDialog(
      `¿Confirmar a ${name} como ganador del sorteo de "${group.amenityName}" el ${group.date}?` +
      (cost > 0 ? ` Se le cargará ${formatMoney(cost, currency)} en sus gastos comunes. Los demás anotados NO pagan.` : " Sin costo."),
      { title: "Confirmar ganador del sorteo", confirmLabel: "Confirmar ganador" }
    );
    if (!ok) return;
    setRaffleBusy(group.key);
    try {
      const [y, m] = group.date.split("-").map(Number);
      const batch = writeBatch(db);
      const reservaRef = doc(collection(db, "reservas"));
      batch.set(reservaRef, {
        buildingid: Number(buildingFilter),
        amenity: group.amenityName,
        amenityId: group.amenityId || config?.id || "",
        date: group.date,
        turno: "Sorteo",
        userid: winnerUserid,
        apartmentId: winnerEntry.apartmentId || "",
        status: "confirmed",
        fromRaffle: true,
        createdBy: auth.currentUser?.email || "admin",
        createdAt: serverTimestamp(),
      });
      if (cost > 0) {
        const chargeRef = doc(collection(db, "amenitycharges"));
        batch.set(chargeRef, {
          reservaId: reservaRef.id,
          amenityId: group.amenityId || config?.id || "",
          userid: winnerUserid,
          apartmentId: winnerEntry.apartmentId || "",
          buildingid: Number(buildingFilter),
          amenity: group.amenityName,
          turno: "Sorteo",
          date: group.date,
          cost,
          currency,
          concept: `Sorteo ${group.amenityName} - ${group.date} (ganador)`,
          residentType: getResidentType(winnerUserid),
          status: "pending",
          month: m,
          year: y,
          targetFundId: config?.targetFundId || "reserva",
          createdAt: serverTimestamp(),
        });
      }
      // Marcar anotaciones: ganador / no_seleccionado (los que no ganaron NO pagan).
      group.entries.forEach((e) => {
        batch.update(doc(db, "amenity_signups", e.id), {
          status: claveId(e.userid) === claveId(winnerUserid) ? "ganador" : "no_seleccionado",
          resolvedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      showToast(`${name} ganó el sorteo.${cost > 0 ? " Se le aplicó el cargo." : ""}`, "success");
      setRaffleWinners((prev) => { const n = { ...prev }; delete n[group.key]; return n; });
      fetchData();
    } catch (e) {
      console.error("Error confirmando ganador:", e);
      showToast("No se pudo confirmar el ganador.", "error");
    } finally {
      setRaffleBusy("");
    }
  };

  // ============================================================
  // Render: sin edificio seleccionado
  // ============================================================
  if (!buildingFilter) {
    return (
      <div style={styles.container}>
        <ScreenHeader icon={FaCalendarAlt} title="Reservas Amenities" />
        <div style={styles.emptyState}>
          <FaSwimmingPool size={48} color={colors.primary} style={{ opacity: 0.4 }} />
          <h2 style={styles.emptyTitle}>Selecciona un Edificio</h2>
          <p style={styles.emptyMessage}>Utiliza el filtro en la barra superior.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <ScreenHeader icon={FaCalendarAlt} title="Reservas Amenities" />

      {/* Barra de resumen */}
      <div style={styles.summaryBar}>
        <span style={styles.summaryItem}>
          Reservas: <strong>{totalReservas}</strong>
        </span>
        <span style={styles.summaryDivider}>|</span>
        <span style={styles.summaryItem}>
          Bloqueados: <strong>{totalBlocked}</strong>
        </span>
        <span style={styles.summaryDivider}>|</span>
        <span style={styles.summaryItem}>
          Ingresos estimados: <strong>{formatMoney(ingresosEstimados, "UYU")}</strong>
        </span>
      </div>

      {/* Filtros + navegación de mes */}
      <div style={styles.controlsRow}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <FaFilter style={{ color: colors.gray600, fontSize: typography.sizes.sm }} />
            <select
              style={styles.filterSelect}
              value={filterAmenity}
              onChange={(e) => setFilterAmenity(e.target.value)}
              aria-label="Filtrar por amenity"
            >
              {amenityNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <FaHome style={{ color: colors.gray600, fontSize: typography.sizes.sm }} />
            <select
              style={styles.filterSelect}
              value={filterUnit}
              onChange={(e) => setFilterUnit(e.target.value)}
              aria-label="Filtrar por unidad"
            >
              {unitNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.monthNav}>
          <button style={styles.navBtn} onClick={goToPrevMonth} aria-label="Mes anterior">
            <FaChevronLeft />
          </button>
          <span style={styles.monthLabel}>
            {monthNames[calendarMonth]} {calendarYear}
          </span>
          <button style={styles.navBtn} onClick={goToNextMonth} aria-label="Mes siguiente">
            <FaChevronRight />
          </button>
        </div>
        <button
          style={{ ...btnPrimary, fontSize: typography.sizes.sm, padding: "7px 14px", display: "flex", alignItems: "center", gap: 6 }}
          onClick={() => {
            setReserveDate(null);
            setReserveFullDate("");
            setReserveTurn("");
            setReserveApartment("");
            setReserveResident("");
            setReserveByUnit(false);
            setReserveAmenityName(filterAmenity !== "Todos" ? filterAmenity : amenities[0]?.name || "");
            setShowReserveModal(true);
          }}
        >
          <FaPlus size={12} /> Nueva reserva
        </button>
      </div>

      {loading ? (
        <p style={styles.loadingText}>Cargando reservas...</p>
      ) : (
        <>
          {/* Grilla del calendario */}
          <div style={styles.calendarGrid}>
            {/* Encabezados de día */}
            {dayNames.map((d) => (
              <div key={d} style={styles.dayHeader}>{d}</div>
            ))}
            {/* Celdas de día */}
            {calendarDays.map((day, idx) => {
              if (day === null) return <div key={`blank-${idx}`} style={styles.dayCell} />;

              const available = isDayAvailable(day);
              const dayReservas = reservasByDay[day] || [];
              const hasBlocked = dayReservas.some((r) => r.status === "blocked");
              const hasReserved = dayReservas.some((r) => r.status !== "blocked");

              // Determinar color de fondo y borde según estado
              let cellBg = "transparent";
              let cellBorder = "1px solid transparent";
              if (!available) {
                cellBg = colors.gray100;
              } else if (hasBlocked && hasReserved) {
                cellBorder = `1px solid ${colors.warning}`;
                cellBg = colors.warningLight;
              } else if (hasBlocked) {
                cellBorder = `1px solid ${colors.warning}`;
                cellBg = colors.warningLight;
              } else if (hasReserved) {
                cellBorder = `1px solid ${colors.error}`;
                cellBg = colors.errorLight;
              }

              return (
                <div
                  key={day}
                  style={{
                    ...styles.dayCell,
                    backgroundColor: cellBg,
                    border: cellBorder,
                    cursor: available ? "pointer" : "default",
                    opacity: available ? 1 : 0.5,
                  }}
                  onClick={() => {
                    if (!available) return;
                    setSelectedDayDetail({ day, reservas: dayReservas });
                  }}
                >
                  <span style={styles.dayNumber}>{day}</span>
                  {/* Indicadores de turnos */}
                  {available && (
                    <div style={styles.turnsIndicator}>
                      {activeTurns.map((turn) => {
                        const slot = getSlotStatus(day, turn.name);
                        // Verde = disponible, amarillo = bloqueado, rojo = reservado
                        let dotColor = colors.success;
                        if (slot.status === "blocked") dotColor = colors.warning;
                        else if (slot.status === "reserved") dotColor = colors.error;

                        return (
                          <div key={turn.name} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <span style={{ ...styles.dot, backgroundColor: dotColor }} />
                            <span style={styles.turnLabel}>{turn.name.substring(0, 3)}</span>
                            {slot.status === "reserved" && !selectedAmenityConfig?.exclusive && slot.count > 0 && (
                              <span style={styles.countBadge}>{slot.count}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!available && <span style={styles.unavailableLabel}>No disp.</span>}
                </div>
              );
            })}
          </div>

          {/* Leyenda */}
          <div style={styles.legend}>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: colors.success }} /> Disponible
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: colors.error }} /> Reservado
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: colors.warning }} /> Bloqueado
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: colors.gray300 }} /> No disponible
            </span>
          </div>
        </>
      )}

      {/* ============ R6+: SORTEOS DE AMENITIES ============ */}
      {raffleGroups.length > 0 && (
        <div style={{ marginTop: spacing[6] }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[4] }}>
            <FaTicketAlt style={{ color: colors.primary, fontSize: typography.sizes.base }} />
            <span style={{ fontSize: typography.sizes.lg, color: colors.black }}>Sorteos de amenities</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
            {raffleGroups.map((g) => {
              const tentative = raffleWinners[g.key];
              const { cost, currency } = raffleCostFor(g.amenityName);
              return (
                <div key={g.key} style={{ ...cardBase, padding: spacing[5] }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: spacing[3] }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: spacing[2], flexWrap: "wrap" }}>
                        <span style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>{g.amenityName}</span>
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{g.date}</span>
                      </div>
                      <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 2 }}>
                        {g.participantes.length} anotado{g.participantes.length === 1 ? "" : "s"} · {cost > 0 ? `costo ${formatMoney(cost, currency)}` : "sin costo"}
                      </div>
                    </div>
                    {g.resolved ? (
                      <span style={{ ...badgeBase, background: colors.successLight, color: colors.success }}>
                        <FaTrophy style={{ fontSize: 11 }} /> Ganador: {usersMap[String(g.winner?.userid)]?.name || `ID ${g.winner?.userid}`}
                      </span>
                    ) : (
                      <div style={{ display: "flex", gap: spacing[2] }}>
                        <button type="button" onClick={() => handleSortear(g)} disabled={raffleBusy === g.key}
                          style={{ ...btnSecondary, display: "inline-flex", alignItems: "center", gap: 6, fontSize: typography.sizes.sm, padding: "6px 12px" }}>
                          <FaRandom style={{ fontSize: 11 }} /> {tentative ? "Volver a sortear" : "Sortear"}
                        </button>
                        {tentative && (
                          <button type="button" onClick={() => handleConfirmWinner(g)} disabled={raffleBusy === g.key}
                            style={{ ...btnPrimary, fontSize: typography.sizes.sm, padding: "6px 12px" }}>
                            {raffleBusy === g.key ? "Confirmando…" : "Confirmar ganador"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: spacing[4], display: "flex", flexWrap: "wrap", gap: spacing[2] }}>
                    {g.participantes.map((e) => {
                      const isWinner = g.resolved ? claveId(e.userid) === claveId(g.winner?.userid) : claveId(e.userid) === claveId(tentative);
                      const isLoser = g.resolved && !isWinner;
                      return (
                        <span key={e.id} style={{
                          padding: "4px 12px", borderRadius: radius.full, fontSize: typography.sizes.sm,
                          background: isWinner ? colors.successLight : colors.gray50,
                          color: isWinner ? colors.success : isLoser ? colors.gray400 : colors.gray700,
                          textDecoration: isLoser ? "line-through" : "none",
                        }}>
                          {usersMap[String(e.userid)]?.name || `ID ${e.userid}`}{e.unitNumber ? ` · ${e.unitNumber}` : ""}
                        </span>
                      );
                    })}
                  </div>
                  {!g.resolved && tentative && (
                    <div style={{ marginTop: spacing[3], fontSize: typography.sizes.sm, color: colors.gray600 }}>
                      Sorteado (tentativo): {usersMap[String(tentative)]?.name || `ID ${tentative}`}. Confirmá para asignarle la reserva y el cargo, o volvé a sortear.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ MODAL DETALLE DEL DÍA ============ */}
      <AdminModal
        open={!!selectedDayDetail}
        onClose={() => setSelectedDayDetail(null)}
        title={
          selectedDayDetail
            ? `${selectedDayDetail.day} de ${monthNames[calendarMonth]} ${calendarYear}${filterAmenity !== "Todos" ? ` — ${filterAmenity}` : ""}`
            : ""
        }
        size="medium"
        footer={
          filterAmenity !== "Todos" && selectedDayDetail ? (
            <div style={{ display: "flex", gap: spacing[2], width: "100%" }}>
              <button
                style={styles.actionBtnSmall}
                onClick={() => {
                  setBlockDate(selectedDayDetail.day);
                  setShowBlockModal(true);
                  setSelectedDayDetail(null);
                }}
              >
                <FaBan style={{ marginRight: 4 }} /> Bloquear
              </button>
              <button
                style={{ ...btnPrimary, fontSize: typography.sizes.sm, padding: "6px 12px" }}
                onClick={() => {
                  setReserveDate(selectedDayDetail.day);
                  setReserveTurn("");
                  setReserveApartment("");
                  setReserveResident("");
                  setReserveByUnit(false);
                  setReserveAmenityName(filterAmenity !== "Todos" ? filterAmenity : amenities[0]?.name || "");
                  setShowReserveModal(true);
                  setSelectedDayDetail(null);
                }}
              >
                <FaPlus style={{ marginRight: 4 }} /> Reservar
              </button>
            </div>
          ) : null
        }
      >
        {selectedDayDetail && selectedDayDetail.reservas.length === 0 ? (
          <p style={{ color: colors.gray600, textAlign: "center", padding: spacing[5] }}>
            No hay reservas ni bloqueos para este día.
          </p>
        ) : (
          selectedDayDetail && selectedDayDetail.reservas.map((r, i) => {
            const costInfo = getReservaCost(r);
            const isBlocked = r.status === "blocked";
            const isResByUnit = r.byUnit === true;
            const residentName = isResByUnit ? "—" : (usersMap[String(r.userid)]?.name || r.userid || "—");
            const residentUnit = isResByUnit ? getUnitNumberById(r.apartmentId) : getResidentUnit(r.userid);
            const residentType = isResByUnit ? "—" : getResidentType(r.userid);

            return (
              <div key={i} style={{
                ...styles.detailCard,
                borderLeft: `4px solid ${isBlocked ? colors.warning : colors.error}`,
              }}>
                {isBlocked ? (
                  <>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Estado:</span>
                      <span style={{ ...styles.statusBadge, backgroundColor: colors.warningLight, color: colors.warningDark }}>
                        Bloqueado
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Turno:</span>
                      <span>{r.turno}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Motivo:</span>
                      <span>{r.blockReason || "Sin motivo"}</span>
                    </div>
                    <button style={styles.cancelBtnSmall} onClick={() => handleUnblock(r)}>
                      Desbloquear
                    </button>
                  </>
                ) : (
                  <>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Amenity:</span>
                      <span>{r.amenity}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Turno:</span>
                      <span>{r.turno}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Residente:</span>
                      <span>{residentName}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Unidad:</span>
                      <span>{residentUnit}</span>
                      {residentType !== "—" && (
                        <span style={{
                          ...styles.typeBadge,
                          backgroundColor: residentType === "Propietario" ? STATUS_COLORS.propietario.bg : STATUS_COLORS.inquilino.bg,
                          color: residentType === "Propietario" ? STATUS_COLORS.propietario.text : STATUS_COLORS.inquilino.text,
                        }}>
                          {residentType}
                        </span>
                      )}
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Costo:</span>
                      <span>
                        {costInfo.cost > 0
                          ? formatMoney(costInfo.cost, costInfo.currency || "UYU")
                          : "Sin costo"}
                      </span>
                    </div>
                    <button style={styles.cancelBtnSmall} onClick={() => handleCancelReservation(r)}>
                      <FaTrash style={{ marginRight: 4 }} /> Cancelar reserva
                    </button>
                  </>
                )}
              </div>
            );
          })
        )}
      </AdminModal>

      {/* ============ MODAL BLOQUEAR TURNO ============ */}
      <AdminModal
        open={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        title="Bloquear turno"
        size="narrow"
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button style={btnSecondary} onClick={() => setShowBlockModal(false)}>
              Cancelar
            </button>
            <button style={btnPrimary} onClick={handleBlockSlot}>
              <FaBan style={{ marginRight: 4 }} /> Bloquear
            </button>
          </div>
        }
      >
        <p style={{ fontSize: typography.sizes.md, color: colors.gray700, margin: 0 }}>
          {blockDate} de {monthNames[calendarMonth]} {calendarYear} — {filterAmenity}
        </p>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Turno</label>
          <select
            style={styles.formInput}
            value={blockTurn}
            onChange={(e) => setBlockTurn(e.target.value)}
            aria-label="Seleccionar turno a bloquear"
          >
            <option value="">Todo el día</option>
            {activeTurns.map((t) => (
              <option key={t.name} value={t.name}>{t.name} ({t.startTime}–{t.endTime})</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Motivo *</label>
          <input
            style={styles.formInput}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Ej: Mantenimiento, evento privado..."
            aria-label="Motivo del bloqueo"
          />
        </div>
      </AdminModal>

      {/* ============ MODAL RESERVAR EN NOMBRE DE RESIDENTE ============ */}
      <AdminModal
        open={showReserveModal}
        onClose={() => setShowReserveModal(false)}
        title="Reservar en nombre de residente"
        size="narrow"
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button style={btnSecondary} onClick={() => setShowReserveModal(false)}>
              Cancelar
            </button>
            <button style={btnPrimary} onClick={handleReserveOnBehalf}>
              <FaPlus style={{ marginRight: 4 }} /> Crear reserva
            </button>
          </div>
        }
      >
        {/* Amenity */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Amenity *</label>
          <select
            style={styles.formInput}
            value={reserveAmenityName}
            onChange={(e) => { setReserveAmenityName(e.target.value); setReserveTurn(""); }}
            aria-label="Seleccionar amenity"
          >
            <option value="">Seleccionar amenity...</option>
            {amenities.map((a) => (
              <option key={a.name || a.amenity} value={a.name || a.amenity}>
                {a.name || a.amenity}
              </option>
            ))}
          </select>
        </div>

        {/* Fecha */}
        {reserveDate ? (
          <p style={{ fontSize: typography.sizes.md, color: colors.gray700, margin: "0 0 12px" }}>
            {reserveDate} de {monthNames[calendarMonth]} {calendarYear}
          </p>
        ) : (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Fecha *</label>
            <input
              type="date"
              style={styles.formInput}
              value={reserveFullDate}
              onChange={(e) => setReserveFullDate(e.target.value)}
              aria-label="Fecha de reserva"
            />
          </div>
        )}

        {/* Turno */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Turno *</label>
          <select
            style={styles.formInput}
            value={reserveTurn}
            onChange={(e) => setReserveTurn(e.target.value)}
            aria-label="Seleccionar turno"
          >
            <option value="">Seleccionar turno...</option>
            {modalTurns.map((t) => (
              <option key={t.name} value={t.name}>{t.name} ({t.startTime}–{t.endTime})</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Unidad *</label>
          <select
            style={styles.formInput}
            value={reserveApartment}
            onChange={(e) => {
              setReserveApartment(e.target.value);
              // Auto-seleccionar residente (propietario o inquilino)
              const apt = apartmentsMap[e.target.value];
              if (apt && !reserveByUnit) {
                setReserveResident(String(apt.tenant || apt.owner || ""));
              }
            }}
            aria-label="Seleccionar unidad"
          >
            <option value="">Seleccionar unidad...</option>
            {Object.values(apartmentsMap)
              .sort((a, b) => (a.name || a.number || "").localeCompare(b.name || b.number || ""))
              .map((apt) => (
                <option key={apt.id} value={apt.id}>
                  {apt.name || apt.unit || apt.number || apt.id}
                </option>
              ))}
          </select>
        </div>

        {/* Checkbox: asociar a unidad sin residente específico */}
        <div style={{ ...styles.formGroup, flexDirection: "row", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            id="reserveByUnit"
            checked={reserveByUnit}
            onChange={(e) => {
              setReserveByUnit(e.target.checked);
              if (e.target.checked) setReserveResident("");
            }}
            style={{ width: 16, height: 16, cursor: "pointer", accentColor: colors.primary }}
          />
          <label htmlFor="reserveByUnit" style={{ ...styles.formLabel, margin: 0, cursor: "pointer" }}>
            Asociar a unidad (sin asignar residente)
          </label>
        </div>
        {reserveByUnit && (
          <p style={{ fontSize: typography.sizes.sm, color: colors.warning, margin: "4px 0 12px", display: "flex", alignItems: "center", gap: 6 }}>
            <FaBan size={11} /> Al cerrar el mes se te pedirá asignar este cargo a un propietario o inquilino.
          </p>
        )}

        {reserveApartment && !reserveByUnit && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Residente</label>
            <select
              style={styles.formInput}
              value={reserveResident}
              onChange={(e) => setReserveResident(e.target.value)}
              aria-label="Seleccionar residente"
            >
              <option value="">Seleccionar...</option>
              {(() => {
                const apt = apartmentsMap[reserveApartment];
                if (!apt) return null;
                const options = [];
                if (apt.owner) {
                  const u = usersMap[String(apt.owner)];
                  options.push(
                    <option key={`owner-${apt.owner}`} value={String(apt.owner)}>
                      {u?.name || apt.owner} (Propietario)
                    </option>
                  );
                }
                if (apt.ownerSecondary && String(apt.ownerSecondary) !== String(apt.owner)) {
                  const u = usersMap[String(apt.ownerSecondary)];
                  options.push(
                    <option key={`owner2-${apt.ownerSecondary}`} value={String(apt.ownerSecondary)}>
                      {u?.name || apt.ownerSecondary} (Propietario complementario)
                    </option>
                  );
                }
                if (apt.tenant && String(apt.tenant) !== String(apt.owner)) {
                  const u = usersMap[String(apt.tenant)];
                  options.push(
                    <option key={`tenant-${apt.tenant}`} value={String(apt.tenant)}>
                      {u?.name || apt.tenant} (Inquilino)
                    </option>
                  );
                }
                if (
                  apt.tenantSecondary &&
                  String(apt.tenantSecondary) !== String(apt.tenant) &&
                  String(apt.tenantSecondary) !== String(apt.owner) &&
                  String(apt.tenantSecondary) !== String(apt.ownerSecondary)
                ) {
                  const u = usersMap[String(apt.tenantSecondary)];
                  options.push(
                    <option key={`tenant2-${apt.tenantSecondary}`} value={String(apt.tenantSecondary)}>
                      {u?.name || apt.tenantSecondary} (Inquilino complementario)
                    </option>
                  );
                }
                return options;
              })()}
            </select>
          </div>
        )}
      </AdminModal>

      {/* Modal de cancelación con motivo */}
      <AdminModal
        open={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelTarget(null); setCancelReason(""); }}
        title="Cancelar reserva"
        icon={<FaBan />}
        size="narrow"
        footer={
          <>
            <button
              style={btnSecondary}
              className="habitia-btn-secondary"
              onClick={() => { setShowCancelModal(false); setCancelTarget(null); setCancelReason(""); }}
            >
              Volver
            </button>
            <button
              style={{ ...btnPrimary, backgroundColor: colors.error }}
              className="habitia-btn-danger"
              onClick={confirmCancelReservation}
            >
              Cancelar reserva
            </button>
          </>
        }
      >
        {cancelTarget && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <div style={{ fontSize: typography.sizes.md, color: colors.gray900 }}>
              ¿Cancelar la reserva de <span style={{ color: colors.black }}>{usersMap[String(cancelTarget.userid)]?.name || cancelTarget.userid}</span>?
            </div>
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", flexDirection: "column", gap: spacing[1] }}>
              <span>Amenity: {cancelTarget.amenity}</span>
              <span>Fecha: {cancelTarget.date} — Turno: {cancelTarget.turno}</span>
              {cancelTarget.cost > 0 && <span>El cargo de {formatMoney(cancelTarget.cost, cancelTarget.currency || "UYU")} será revertido.</span>}
            </div>
            <div>
              <label style={{ fontSize: typography.sizes.sm, color: colors.gray900, display: "block", marginBottom: spacing[1] }}>
                Motivo de cancelación *
              </label>
              <textarea
                style={{ ...inputBase, minHeight: 80, resize: "vertical", width: "100%" }}
                className="habitia-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: Amenity fuera de servicio por mantenimiento"
              />
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

// ============================================================
// Estilos — basados en design tokens
// ============================================================
const styles = {
  // Contenedor principal de página
  container: {
    ...pageContainer,
  },
  // Estado vacío (sin edificio seleccionado)
  emptyState: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "50vh",
    textAlign: "center",
  },
  emptyTitle: {
    ...pageTitle,
    color: colors.black,
    margin: `${spacing[4]}px 0 ${spacing[2]}px`,
  },
  emptyMessage: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  // Barra de resumen
  summaryBar: {
    ...cardBase,
    display: "flex",
    gap: spacing[3],
    alignItems: "center",
    padding: `${spacing[4]}px ${spacing[5]}px`,
    marginBottom: spacing[4],
    fontSize: typography.sizes.md,
    color: colors.gray700,
    flexWrap: "wrap",
  },
  summaryItem: { display: "flex", gap: 4, alignItems: "center" },
  summaryDivider: { color: colors.gray300 },
  // Controles (filtros + navegación)
  controlsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[4],
    gap: spacing[3],
    flexWrap: "wrap",
  },
  filtersRow: {
    display: "flex",
    gap: spacing[3],
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  // Select de filtro
  filterSelect: {
    ...inputBase,
    width: "auto",
    minWidth: 140,
    padding: "10px 14px",
    borderRadius: radius.sm + 2,
    color: colors.black,
  },
  // Navegación de mes
  monthNav: { display: "flex", alignItems: "center", gap: spacing[3] },
  navBtn: {
    ...btnGhost,
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    backgroundColor: colors.white,
    color: colors.primary,
    fontSize: typography.sizes.md,
  },
  monthLabel: {
    fontSize: typography.sizes.lg,
    color: colors.black,
    minWidth: 160,
    textAlign: "center",
  },
  // Texto de carga
  loadingText: {
    textAlign: "center",
    color: colors.gray600,
    padding: spacing[10],
    fontStyle: "italic",
  },
  // Grilla del calendario
  calendarGrid: {
    ...cardBase,
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
    padding: spacing[3],
  },
  // Encabezado de día de la semana
  dayHeader: {
    textAlign: "center",
    padding: `${spacing[2]}px 0`,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    textTransform: "uppercase",
  },
  // Celda de día
  dayCell: {
    minHeight: 80,
    borderRadius: radius.sm,
    padding: spacing[2],
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    transition: `all ${transitions.fast}`,
    position: "relative",
  },
  // Número del día
  dayNumber: {
    fontSize: typography.sizes.base,
    color: colors.black,
  },
  // Contenedor de indicadores de turno
  turnsIndicator: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
    paddingTop: 2,
  },
  // Punto de estado
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  },
  // Etiqueta de turno abreviada
  turnLabel: {
    fontSize: 9,
    color: colors.gray600,
  },
  // Badge de conteo de reservas en turno
  countBadge: {
    fontSize: 9,
    color: colors.primary,
    backgroundColor: colors.primaryLighter,
    borderRadius: 4,
    padding: "0 4px",
  },
  // Etiqueta de no disponible
  unavailableLabel: {
    fontSize: 9,
    color: colors.gray400,
    marginTop: 4,
  },
  // Leyenda del calendario
  legend: {
    display: "flex",
    gap: spacing[5],
    justifyContent: "center",
    marginTop: spacing[3],
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  legendItem: { display: "flex", alignItems: "center", gap: spacing[2] },
  // Tarjeta de detalle dentro del modal
  detailCard: {
    padding: spacing[4],
    borderRadius: radius.sm + 2,
    backgroundColor: colors.gray50,
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
  },
  // Fila de detalle (label + valor)
  detailRow: {
    display: "flex",
    gap: spacing[2],
    fontSize: typography.sizes.md,
    color: colors.gray700,
    alignItems: "center",
  },
  // Label dentro de fila de detalle
  detailLabel: {
    minWidth: 80,
    color: colors.black,
  },
  // Badge de estado
  statusBadge: {
    ...badgeBase,
    fontSize: typography.sizes.xs,
    padding: "2px 10px",
  },
  // Badge de tipo de residente
  typeBadge: {
    ...badgeBase,
    fontSize: typography.sizes.xs,
    padding: "2px 8px",
    marginLeft: 4,
  },
  // Botón de acción pequeño (bloquear)
  actionBtnSmall: {
    ...btnSecondary,
    fontSize: typography.sizes.sm,
    padding: "6px 12px",
    borderRadius: radius.sm - 2,
  },
  // Botón cancelar/desbloquear pequeño
  cancelBtnSmall: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: radius.sm - 2,
    border: `1px solid ${colors.error}`,
    backgroundColor: colors.white,
    color: colors.error,
    fontSize: typography.sizes.sm,
    cursor: "pointer",
    alignSelf: "flex-start",
    marginTop: 4,
    fontFamily: typography.fontFamily,
  },
  // Grupo de formulario
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
  },
  // Label de formulario
  formLabel: {
    fontSize: typography.sizes.base,
    color: colors.gray900,
  },
  // Input/select de formulario
  formInput: {
    ...inputBase,
    backgroundColor: colors.gray50,
  },
};

export default ReservasAmenities;
